import dynamic from "next/dynamic";
import fallbackTranslation from "@/lib/dbTranslations";
import useSWR from "swr";
import { NextSeo } from "next-seo";
import { getLocale } from "@/common/utils/sharedFunctions";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useApp } from "@/components/context/AppContext/AppContext";
import { useAuth } from "@/components/auth/hooks/useAuth";
import { useAuthUser, withAuthUser } from "next-firebase-auth";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { i18n, useTranslation } from "next-i18next";
import lodash, { orderBy } from "lodash";
// COMPONENTS
import BootstrapSpinner from "@/components/elements/LoadingPlaceholders/BootstrapSpinner";
import GridGenerator from "@/components/core/GridGenerator/GridGenerator";
const Pagination = dynamic(() => import("@/components/core/Catalogo/Pagination/Pagination"), { ssr: false });
import ProductCard from "@/components/core/Catalogo/ProductCard/ProductCard";
import SideFilters from "@/components/core/Catalogo/SideFilters/SideFilters";
import SkeletonProductCard from "@/components/core/Skeletons/SkeletonProductCard";
import TopFilters from "@/components/core/Catalogo/TopFilters/TopFilters";
import { fetcher } from "@/pages/api/util/fetcher";
// BOOTSTRAP
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
// STYLE
import s from "./index.module.scss";

function Catalogue(props) {
  const app = useApp();
  const auth = useAuth();
  const router = useRouter();
  const AuthUser = useAuthUser();
  const { t } = useTranslation(["common", "product-category"]);

  const [enableEditing, setEnableEditing] = useState(false);

  const [clothingProductsGroup, setclothingProductsGroup] = useState([]);

  const [categoryObj, setCategoryObj] = useState({});

  // Set colors and sizes from app context, check if values are ready.
  useEffect(() => {
    if (app.appConfig?.clothingProductsGroup?.length > 0) {
      setclothingProductsGroup(app.appConfig?.clothingProductsGroup);
      setCategoryObj(
        app.appConfig?.clothingProductsGroup.find((el) => el.key == router.query["category"]?.toString().toUpperCase()) ||
        router.query["category"]?.toString()
      );
    }
  }, [app]);

  // If the user is not allowed will be redirect to homepage.
  useEffect(() => {
    if (auth.isUserSupplier()) {
      router.push("/home-supplier");
    }
  }, [[], auth]);

  // if No Page is provided wil start from page 1
  const page = router.query["Page"] ? parseInt(router.query.Page.toString()) : 1;
  const limit = process.env.NEXT_PUBLIC_PAGE_CATALOGUE_SIZE;
  const filter: any = { ...router.query, stato: 1, limit: limit };

  const { data, mutate } = useSWR(auth?.user ? "/api/product/catalogue" + "?" + new URLSearchParams(filter).toString() : null, fetcher, {
    revalidateIfStale: true,
    refreshInterval: 30000,
    fallbackData: { products: props.products, count: props.count },
  });

  // Empty array with same size than page size, used to create a grid and display skeletons  new Array(process.env.NEXT_PUBLIC_PAGE_CATALOGUE_SIZE).fill(undefined);
  // Retrieves products list and the total counter
  const products = data?.products || props?.products || new Array(process.env.NEXT_PUBLIC_PAGE_CATALOGUE_SIZE).fill(undefined);
  const count = data?.count || props?.count || 0;

  let productList = products?.map((prod, index) => {
    if (!prod) {
      return <SkeletonProductCard style={{ width: "100%" }} key={index} />;
    } else {
      return (
        <ProductCard
          index={index}
          enableEditing={enableEditing}
          handleRemoveProduct={handleRemoveProduct}
          product={prod}
          key={prod._id}
          category={
            router.query["GruppoMerceologico"] ? router.query["GruppoMerceologico"] : prod.GruppoMerceologico?.[0]?.toLowerCase() || "category"
          }
        />
      );
    }
  });

  // Check if translation exists, used to show the category description
  const existDescription = i18n.exists("clothing-product-group." + (categoryObj["key"] || categoryObj), { ns: "db-translations" });

  /**
   * Remove products from catalogue
   * @param productId
   * @returns
   */
  function handleRemoveProduct(productId) {
    // Removes first product from current catalogue (user feedback)
    mutate({ products: products.filter((prod) => prod._id != productId) }, false);

    // Then removes product from Db
    return fetcher("/api/product/status", {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ _id: productId, status: 0 }),
    }).then(async (response) => {
      if (response.error) throw new Error(response.error);
    });
  }

  return auth.isUserSupplier() ? (
    <BootstrapSpinner />
  ) : (
    <Container fluid className="px-0" key="catalogPageContainer">
      {/* SEO  TODO: Add Image*/}
      <NextSeo
        title={fallbackTranslation("clothing-product-group", "db-translations", categoryObj, t, getLocale(router.locale))}
        description={existDescription ? t(`${categoryObj["key"] || categoryObj}.description`, { ns: "product-category" }) : ""}
        openGraph={{
          type: "website",
          title: fallbackTranslation("clothing-product-group", "db-translations", categoryObj, t, getLocale(router.locale), true),
          description: "",
          // Not all pages have a category, so we need to check if the category is a string or an object
          url: `${process.env.NEXT_PUBLIC_BASE_URL}/catalogue/${categoryObj["key"] ? categoryObj["key"].toLowerCase() : categoryObj}?Page=1&orderBy=LATEST-ARRIVALS`,
        }}
      />
      <Row key="catalogPage" className="mx-0 px-3">
        <Col className={`${s.sidefilters}`}>
          <SideFilters basePath={"catalogue"} clothingProductsGroup={clothingProductsGroup} locale={getLocale(router.locale)} />
        </Col>
        <Col className="px-0">
          {!lodash.isEmpty(categoryObj) ? (
            <>
              <div className="text-center text-md-start">
                <h1 className="fs-1 fw-medium text-uppercase">
                  {router.query.title && t(router.query.title) + " "}
                  {router.query.season
                    ? t("common.collection") + " " + router.query.season
                    : fallbackTranslation("clothing-product-group", "db-translations", categoryObj, t, router.locale, true)}
                </h1>
              </div>
              {existDescription && (
                <div style={{ maxWidth: "50rem" }}>
                  <p className="d-flex flex-column">
                    <span className="fw-medium fs-7">{t(`${categoryObj["key"] || categoryObj}.description`, { ns: "product-category" })}</span>
                  </p>
                </div>
              )}
            </>
          ) : (
            <div style={{ height: "125px" }}></div>
          )}
          {/* If user is admin, display the button to enable editing */}
          {auth.isUserAdmin() && (
            <div className="d-flex justify-content-end">
              <Button variant="link" className="py-0" onClick={() => setEnableEditing(!enableEditing)}>
                {enableEditing ? "Disabilita modifica" : "Abilita modifica"}
              </Button>
            </div>
          )}
          {/* If user is logged in and it's a "primary" category, not a section (novità, promozioni, stagione, etc.), display the top filters */}
          {auth.user && (categoryObj === "ALL_PRODUCTS" || categoryObj["key"]) ? (
            <Row>
              <TopFilters clothingProductsGroup={clothingProductsGroup} colors={app.appConfig?.colors} sizes={app.appConfig?.sizes} />
            </Row>
          ) : (
            <div style={{ height: "65px" }}></div>
          )}
          {/* Display the products list */}
          <Row className="mx-0 justify-content-center">
            {productList.length == 0 ? (
              <div className={s.noItemsMessage}>
                <h3>{t("catalogue.no-products-found")}</h3>
              </div>
            ) : (
              <GridGenerator limit={12}>{productList}</GridGenerator>
            )}
          </Row>
          {/* Pagination */}
          <Row className={"d-flex mx-0 justify-content-center page" + count}>
            {AuthUser.firebaseUser && (
              <Pagination
                count={count}
                pageNum={page - 1}
                pageSize={process.env.NEXT_PUBLIC_PAGE_CATALOGUE_SIZE}
                // Sets the offset
                onPageChange={(offset) => {
                  router.push({
                    query: { ...router.query, Page: offset + 1 },
                  });
                }}
              />
            )}
          </Row>
        </Col>
      </Row>
    </Container>
  );
}

// Init with value first from server side
export async function getServerSideProps({ locale, query, req }) {
  const page = query.Page ? parseInt(query.Page.toString()) : 1;
  const limit = process.env.NEXT_PUBLIC_PAGE_CATALOGUE_SIZE;
  const filter: any = { ...query, stato: 1, Page: page, limit: limit };
  const catalogue = await fetcher("/api/product/catalogue" + "?" + new URLSearchParams(filter).toString(), { cookies: req.cookies });
  return {
    props: {
      ...catalogue,
      ...(await serverSideTranslations(locale, ["common", "header", "footer", "product-detail", "db-translations", "product-category"])),
    },
  };
}

export default withAuthUser({})(Catalogue);
